import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/authService';
import { setTokenExpiredCallback } from '../lib/apiClient';

export interface User {
    id: string;
    name: string;
    phone: string;
    role: string;
}

interface AuthContextType {
    user: User | null;
    login: (username: string, password: string, remember: boolean) => Promise<boolean>;
    logout: () => void;
    isLoading: boolean;
    isInitializing: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

interface AuthProviderProps {
    children: ReactNode;
}

// Inner component that has access to useNavigate
const AuthProviderInner: React.FC<AuthProviderProps> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isInitializing, setIsInitializing] = useState(true);
    const navigate = useNavigate();

    // Load persisted user on mount
    useEffect(() => {
        try {
            // Prefer sessionStorage (non-remembered sessions)
            const sessionUser = sessionStorage.getItem('auth_user');
            if (sessionUser) {
                const parsed: User = JSON.parse(sessionUser);
                setUser(parsed);
                return;
            }

            // Fallback to localStorage (remembered sessions) with expiry check
            const stored = localStorage.getItem('auth_user');
            const expiryStr = localStorage.getItem('auth_expiry');
            if (stored && expiryStr) {
                const expiresAt = parseInt(expiryStr, 10);
                if (!isNaN(expiresAt) && Date.now() < expiresAt) {
                    const parsed: User = JSON.parse(stored);
                    setUser(parsed);
                } else {
                    // Expired - clear persisted data
                    localStorage.removeItem('auth_user');
                    localStorage.removeItem('auth_token');
                    localStorage.removeItem('auth_expiry');
                }
            } else if (stored && !expiryStr) {
                // Legacy data without expiry - clear to avoid stale auth
                localStorage.removeItem('auth_user');
                localStorage.removeItem('auth_token');
            }
        } catch (e) {
            // ignore corrupted storage
            console.warn('Failed to parse persisted user');
        } finally {
            setIsInitializing(false);
        }
    }, []);

    // Setup token expiration callback
    useEffect(() => {
        setTokenExpiredCallback(() => {
            console.log('Token expired - forcing logout and redirect to login');
            // Clear all auth data
            setUser(null);
            try {
                localStorage.removeItem('auth_user');
                localStorage.removeItem('auth_token');
                localStorage.removeItem('auth_expiry');
                sessionStorage.removeItem('auth_user');
                sessionStorage.removeItem('auth_token');
            } catch (e) {
                // ignore
            }
            // Show notification to user
            // alert('Your session has expired. Please login again.');
            // Redirect to login
            navigate('/login', { replace: true });
        });
    }, [navigate]);

    const login = async (username: string, password: string, remember: boolean): Promise<boolean> => {
        setIsLoading(true);
        try {
            const result = await authService.login(username, password);
            if (result) {
                const newUser: User = { id: result.id, name: result.name, phone: result.phone, role: result.role };
                setUser(newUser);
                try {
                    // Clear any previous persisted state
                    localStorage.removeItem('auth_user');
                    localStorage.removeItem('auth_token');
                    localStorage.removeItem('auth_expiry');
                    sessionStorage.removeItem('auth_user');
                    sessionStorage.removeItem('auth_token');

                    if (remember) {
                        // Persist to localStorage with 60-month expiry
                        const expiryDate = new Date();
                        expiryDate.setMonth(expiryDate.getMonth() + 60);
                        localStorage.setItem('auth_user', JSON.stringify(newUser));
                        if (result.token) localStorage.setItem('auth_token', result.token);
                        localStorage.setItem('auth_expiry', String(expiryDate.getTime()));
                    } else {
                        // Store in sessionStorage only
                        sessionStorage.setItem('auth_user', JSON.stringify(newUser));
                        if (result.token) sessionStorage.setItem('auth_token', result.token);
                    }
                } catch {}
                return true;
            }
            return false;
        } finally {
            setIsLoading(false);
        }
    };

    const logout = () => {
        setUser(null);
        try {
            localStorage.removeItem('auth_user');
            localStorage.removeItem('auth_token');
            localStorage.removeItem('auth_expiry');
            sessionStorage.removeItem('auth_user');
            sessionStorage.removeItem('auth_token');
        } catch (e) {
            // ignore
        }
        navigate('/login', { replace: true });
    };

    const value = {
        user,
        login,
        logout,
        isLoading,
        isInitializing,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    return <AuthProviderInner>{children}</AuthProviderInner>;
};
